import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import CloseIcon from '@mui/icons-material/Close';
import { BaseStyleEnums, ButtonType, SelectTypes } from '@nhl/nhl-breakaway-ui';
import { useCloseable } from '@nhl/react-hooks/lib/useCloseable';

import CommonMessages from '~/core/commonMessages';
import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';

import { DraftFilters } from '../../enums';
import { FilterSelect } from '../../styles';
import { DraftResponse, Filter, ResolvedValues } from '../../types';
import { ALL_VALUE, createSecondaryOptions, DEFAULT_ROUND, formatFilters, PAGE_INDEX_QUERY } from '../../utils';

import {
    DialogContainer,
    FilterActionContainer,
    FilterButton,
    FilterModalHeading,
    FiltersContainer,
    SmallScreenFilters,
} from './styles';

type FilterProps = {
    data: DraftResponse;
    resolvedValues: ResolvedValues;
    setIsYearChanging: (value: boolean) => void;
};

const Filters: FC<FilterProps> = ({ data, resolvedValues, setIsYearChanging }) => {
    const intl = useIntl();
    const size = useSizeWithThrottle();
    const navigate = useNavigate();
    const dialogRef = useRef<HTMLDialogElement>(null);
    const isMedium = size.width >= BaseStyleEnums.ScreenWidthBreakpoints.medium;

    const [yearOptions, setYearOptions] = useState<Array<SelectTypes.BasicOption>>([]);
    const [roundOptions, setRoundOptions] = useState<Array<SelectTypes.BasicOption>>([]);
    const [teamOptions, setTeamOptions] = useState<Array<SelectTypes.BasicOption>>([]);
    const [leagueOptions, setLeagueOptions] = useState<Array<SelectTypes.BasicOption>>([]);
    const [amateurTeamOptions, setAmateurTeamOptions] = useState<Array<SelectTypes.BasicOption>>([]);
    const [countryOptions, setCountryOptions] = useState<Array<SelectTypes.BasicOption>>([]);
    const [positionOptions, setPositionOptions] = useState<Array<SelectTypes.BasicOption>>([]);

    const DEFAULT_ALL = useMemo<SelectTypes.BasicOption>(
        () => ({
            label: intl.formatMessage(CommonMessages.misc.allOptions),
            key: ALL_VALUE,
            value: ALL_VALUE,
        }),
        [intl]
    );

    const [expandedFilters, setExpandedFilters] = useState(isMedium);

    const handleModalClose = () => {
        dialogRef.current?.close();
        setExpandedFilters(false);
    };

    useCloseable({ closeableRef: dialogRef, closeFunction: handleModalClose, shouldStopListening: !expandedFilters });

    useEffect(() => {
        if (isMedium) {
            setExpandedFilters(false);
        }
    }, [isMedium]);

    useEffect(() => {
        if (expandedFilters) {
            dialogRef.current?.showModal();
        }
    }, [expandedFilters]);

    const urlBase = `/draft/tracker/${resolvedValues.resolvedYear}/${resolvedValues.resolvedRound}`;

    /*
     * Handle year options
     */
    useEffect(() => {
        const newYearOptions =
            data?.draftYears?.map((year) => ({ label: `${year}`, value: year, key: year })).reverse() || [];
        setYearOptions((yo) => (yo.length === newYearOptions.length ? yo : newYearOptions));
    }, [setYearOptions, data.draftYears]);

    /*
     * Handle round options
     */
    useEffect(() => {
        if (data.selectableRounds) {
            const newRoundOptions: Array<SelectTypes.BasicOption> = data.selectableRounds.map((round) => ({
                label: `${round}`,
                value: round,
                key: round,
            }));

            setRoundOptions((ro) => (ro.length === newRoundOptions.length ? ro : newRoundOptions));
        }
    }, [DEFAULT_ALL, setRoundOptions, data.selectableRounds]);

    /*
     * Handle secondary options
     */
    const secondaryOptions = useMemo(() => createSecondaryOptions(data.picks, intl), [data.picks, intl]);

    useEffect(() => {
        setTeamOptions((to) => (to.length === secondaryOptions.teamOptions.length ? to : secondaryOptions.teamOptions));
    }, [secondaryOptions.teamOptions]);

    useEffect(() => {
        setLeagueOptions((lo) =>
            lo.length === secondaryOptions.leagueOptions.length ? lo : secondaryOptions.leagueOptions
        );
    }, [secondaryOptions.leagueOptions]);

    useEffect(() => {
        setAmateurTeamOptions((to) =>
            to.length === secondaryOptions.amateurTeamOptions.length ? to : secondaryOptions.amateurTeamOptions
        );
    }, [secondaryOptions.amateurTeamOptions]);

    useEffect(() => {
        setCountryOptions((co) =>
            co.length === secondaryOptions.countryOptions.length ? co : secondaryOptions.countryOptions
        );
    }, [secondaryOptions.countryOptions]);

    useEffect(() => {
        setPositionOptions((po) =>
            po.length === secondaryOptions.positionOptions.length ? po : secondaryOptions.positionOptions
        );
    }, [secondaryOptions.positionOptions]);

    // Our small screen modal states, we use these to update the url only when the apply filters button is applied
    const [selectedModalTeam, setSelectedModalTeam] = useState<string | number>(resolvedValues.resolvedTeam);
    const [selectedModalLeague, setSelectedModalLeague] = useState<string | number>(resolvedValues.resolvedLeague);
    const [selectedModalAmateurTeam, setSelectedModalAmateurTeam] = useState<string | number>(
        resolvedValues.resolvedAmateurTeam
    );
    const [selectedModalCountry, setSelectedModalCountry] = useState<string | number>(resolvedValues.resolvedCountry);
    const [selectedModalPosition, setSelectedModalPosition] = useState<string | number>(
        resolvedValues.resolvedPosition
    );

    // Apply modal filters
    const applyFilters = () => {
        navigate(
            `${urlBase}/${selectedModalTeam}/${selectedModalLeague}/${selectedModalAmateurTeam}/${selectedModalCountry}/${selectedModalPosition}`
        );
        setDisableApplyFilter(true);
        dialogRef.current?.close();
        setExpandedFilters(false);
    };

    // We want to check if the modals selected are the base all value or not
    const areFiltersAtBaseValue = useMemo(
        () =>
            selectedModalTeam === ALL_VALUE &&
            selectedModalLeague === ALL_VALUE &&
            selectedModalAmateurTeam === ALL_VALUE &&
            selectedModalCountry === ALL_VALUE &&
            selectedModalPosition === ALL_VALUE,
        [selectedModalTeam, selectedModalLeague, selectedModalAmateurTeam, selectedModalCountry, selectedModalPosition]
    );

    const [disableClearFilter, setDisableClearFilter] = useState(areFiltersAtBaseValue);
    const [disableApplyFilter, setDisableApplyFilter] = useState(true);

    useEffect(() => {
        setDisableClearFilter(areFiltersAtBaseValue);
        if (
            selectedModalTeam !== resolvedValues.resolvedTeam ||
            selectedModalLeague !== resolvedValues.resolvedLeague ||
            selectedModalAmateurTeam !== resolvedValues.resolvedAmateurTeam ||
            selectedModalCountry !== resolvedValues.resolvedCountry ||
            selectedModalPosition !== resolvedValues.resolvedPosition
        ) {
            setDisableApplyFilter(false);
        }
    }, [
        areFiltersAtBaseValue,
        resolvedValues,
        selectedModalTeam,
        selectedModalLeague,
        selectedModalAmateurTeam,
        selectedModalCountry,
        selectedModalPosition,
    ]);

    const clearModalFilters = () => {
        setSelectedModalTeam(ALL_VALUE);
        setSelectedModalLeague(ALL_VALUE);
        setSelectedModalAmateurTeam(ALL_VALUE);
        setSelectedModalCountry(ALL_VALUE);
        setSelectedModalPosition(ALL_VALUE);
        setDisableApplyFilter(true);
    };

    const clearFilters = () => {
        navigate(urlBase);
        clearModalFilters();
    };

    const handleYearUpdated = useCallback(
        (e: React.MouseEvent<HTMLInputElement>, year: SelectTypes.BasicOption) => {
            setIsYearChanging(true);
            clearModalFilters();
            navigate(`/draft/tracker/${year.value}/${DEFAULT_ROUND}`);
        },
        [navigate, setIsYearChanging]
    );

    const handleRoundUpdated = useCallback(
        (e: React.MouseEvent<HTMLInputElement>, round: SelectTypes.BasicOption) => {
            clearModalFilters();
            navigate(
                `/draft/tracker/${resolvedValues.resolvedYear}/${round.value}${
                    round.value === ALL_VALUE ? '?' + PAGE_INDEX_QUERY + '=0' : ''
                }`
            );
        },
        [navigate, resolvedValues.resolvedYear]
    );

    const handleTeamUpdated = useCallback(
        (e: React.MouseEvent<HTMLInputElement>, team: SelectTypes.BasicOption) => {
            setSelectedModalTeam(team.value);
            navigate(
                `${urlBase}/${team.value}/${resolvedValues.resolvedLeague}/${resolvedValues.resolvedAmateurTeam}/${resolvedValues.resolvedCountry}/${resolvedValues.resolvedPosition}`
            );
        },
        [navigate, resolvedValues, urlBase]
    );

    const handleLeagueUpdated = useCallback(
        (e: React.MouseEvent<HTMLInputElement>, league: SelectTypes.BasicOption) => {
            setSelectedModalLeague(league.value);
            navigate(
                `${urlBase}/${resolvedValues.resolvedTeam}/${league.value}/${resolvedValues.resolvedAmateurTeam}/${resolvedValues.resolvedCountry}/${resolvedValues.resolvedPosition}`
            );
        },
        [navigate, resolvedValues, urlBase]
    );

    const handleAmateurTeamUpdated = useCallback(
        (e: React.MouseEvent<HTMLInputElement>, amateurTeam: SelectTypes.BasicOption) => {
            setSelectedModalAmateurTeam(amateurTeam.value);
            navigate(
                `${urlBase}/${resolvedValues.resolvedTeam}/${resolvedValues.resolvedLeague}/${amateurTeam.value}/${resolvedValues.resolvedCountry}/${resolvedValues.resolvedPosition}`
            );
        },
        [navigate, resolvedValues, urlBase]
    );

    const handleCountryUpdated = useCallback(
        (e: React.MouseEvent<HTMLInputElement>, country: SelectTypes.BasicOption) => {
            setSelectedModalCountry(country.value);
            navigate(
                `${urlBase}/${resolvedValues.resolvedTeam}/${resolvedValues.resolvedLeague}/${resolvedValues.resolvedAmateurTeam}/${country.value}/${resolvedValues.resolvedPosition}`
            );
        },
        [navigate, resolvedValues, urlBase]
    );

    const handlePositionUpdated = useCallback(
        (e: React.MouseEvent<HTMLInputElement>, position: SelectTypes.BasicOption) => {
            setSelectedModalPosition(position.value);
            navigate(
                `${urlBase}/${resolvedValues.resolvedTeam}/${resolvedValues.resolvedLeague}/${resolvedValues.resolvedAmateurTeam}/${resolvedValues.resolvedCountry}/${position.value}`
            );
        },
        [navigate, resolvedValues, urlBase]
    );

    //These are our modal states, we only update the main filter state with these values when the apply filters button is pressed
    const handleModalTeamUpdated = useCallback(
        (e: React.MouseEvent<HTMLInputElement>, team: SelectTypes.BasicOption) => {
            setSelectedModalTeam(team.value);
        },
        []
    );

    const handleModalLeagueUpdated = useCallback(
        (e: React.MouseEvent<HTMLInputElement>, league: SelectTypes.BasicOption) => {
            setSelectedModalLeague(league.value);
        },
        []
    );

    const handleModalAmateurTeamUpdated = useCallback(
        (e: React.MouseEvent<HTMLInputElement>, amateurTeam: SelectTypes.BasicOption) => {
            setSelectedModalAmateurTeam(amateurTeam.value);
        },
        []
    );

    const handleModalCountryUpdated = useCallback(
        (e: React.MouseEvent<HTMLInputElement>, country: SelectTypes.BasicOption) => {
            setSelectedModalCountry(country.value);
        },
        []
    );

    const handleModalPositionUpdated = useCallback(
        (e: React.MouseEvent<HTMLInputElement>, position: SelectTypes.BasicOption) => {
            setSelectedModalPosition(position.value);
        },
        []
    );

    //Main Filters
    const yearOptionsSelect = useMemo(
        () => (
            <FilterSelect
                aria-label={intl.formatMessage(CommonMessages.tableHeaders.long.year)}
                dropdownButtonAriaLabel={intl.formatMessage(CommonMessages.ariaLabels.select.year.dropdownButton)}
                onOptionChange={handleYearUpdated}
                options={yearOptions}
                optionsBoxAriaLabel={intl.formatMessage(CommonMessages.ariaLabels.select.year.optionsBox)}
                selectedOption={
                    yearOptions.find((yearOption) => yearOption.value === Number(resolvedValues.resolvedYear)) || null
                }
            />
        ),
        [intl, handleYearUpdated, resolvedValues.resolvedYear, yearOptions]
    );

    const roundOptionsSelect = useMemo(
        () => (
            <FilterSelect
                aria-label={intl.formatMessage(CommonMessages.tableHeaders.long.round)}
                dropdownButtonAriaLabel={intl.formatMessage(CommonMessages.ariaLabels.select.round.dropdownButton)}
                onOptionChange={handleRoundUpdated}
                options={roundOptions}
                optionsBoxAriaLabel={intl.formatMessage(CommonMessages.ariaLabels.select.round.optionsBox)}
                selectedOption={
                    roundOptions.find((roundOption) => roundOption.value === Number(resolvedValues.resolvedRound)) ||
                    null
                }
            />
        ),
        [intl, roundOptions, resolvedValues.resolvedRound, handleRoundUpdated]
    );

    // Secondary filters
    const teamOptionsSelect = useMemo(
        () => (
            <FilterSelect
                aria-label={intl.formatMessage(CommonMessages.tableHeaders.long.team)}
                dropdownButtonAriaLabel={intl.formatMessage(CommonMessages.ariaLabels.select.team.dropdownButton)}
                onOptionChange={handleTeamUpdated}
                options={teamOptions}
                optionsBoxAriaLabel={intl.formatMessage(CommonMessages.ariaLabels.select.team.optionsBox)}
                selectedOption={teamOptions.find((to) => to.value === resolvedValues.resolvedTeam) || null}
            />
        ),
        [intl, handleTeamUpdated, teamOptions, resolvedValues.resolvedTeam]
    );

    const leagueOptionsSelect = useMemo(
        () => (
            <FilterSelect
                aria-label={intl.formatMessage(CommonMessages.tableHeaders.long.league)}
                dropdownButtonAriaLabel={intl.formatMessage(CommonMessages.ariaLabels.select.league.dropdownButton)}
                onOptionChange={handleLeagueUpdated}
                options={leagueOptions}
                optionsBoxAriaLabel={intl.formatMessage(CommonMessages.ariaLabels.select.league.optionsBox)}
                selectedOption={leagueOptions.find((lo) => lo.value === resolvedValues.resolvedLeague) || null}
            />
        ),
        [intl, handleLeagueUpdated, leagueOptions, resolvedValues.resolvedLeague]
    );

    const amateurTeamOptionsSelect = useMemo(
        () => (
            <FilterSelect
                aria-label={intl.formatMessage(CommonMessages.tableHeaders.long.amateurTeam)}
                dropdownButtonAriaLabel={intl.formatMessage(
                    CommonMessages.ariaLabels.select.amateurTeam.dropdownButton
                )}
                onOptionChange={handleAmateurTeamUpdated}
                options={amateurTeamOptions}
                optionsBoxAriaLabel={intl.formatMessage(CommonMessages.ariaLabels.select.amateurTeam.optionsBox)}
                selectedOption={
                    amateurTeamOptions.find((ao) => ao.value === resolvedValues.resolvedAmateurTeam) || null
                }
            />
        ),
        [intl, handleAmateurTeamUpdated, amateurTeamOptions, resolvedValues.resolvedAmateurTeam]
    );

    const countryOptionsSelect = useMemo(
        () => (
            <FilterSelect
                aria-label={intl.formatMessage(CommonMessages.tableHeaders.long.country)}
                dropdownButtonAriaLabel={intl.formatMessage(CommonMessages.ariaLabels.select.country.dropdownButton)}
                onOptionChange={handleCountryUpdated}
                options={countryOptions}
                optionsBoxAriaLabel={intl.formatMessage(CommonMessages.ariaLabels.select.country.optionsBox)}
                selectedOption={countryOptions.find((co) => co.value === resolvedValues.resolvedCountry) || null}
            />
        ),
        [intl, handleCountryUpdated, countryOptions, resolvedValues.resolvedCountry]
    );

    const positionOptionsSelect = useMemo(
        () => (
            <FilterSelect
                aria-label={intl.formatMessage(CommonMessages.playerInfoDescriptions.long.position)}
                dropdownButtonAriaLabel={intl.formatMessage(CommonMessages.ariaLabels.select.position.dropdownButton)}
                onOptionChange={handlePositionUpdated}
                options={positionOptions}
                optionsBoxAriaLabel={intl.formatMessage(CommonMessages.ariaLabels.select.position.optionsBox)}
                selectedOption={positionOptions.find((po) => po.value === resolvedValues.resolvedPosition) || null}
            />
        ),
        [intl, handlePositionUpdated, positionOptions, resolvedValues.resolvedPosition]
    );

    // Modal Filters
    const modalTeamOptionsSelect = useMemo(
        () => (
            <FilterSelect
                aria-label={intl.formatMessage(CommonMessages.tableHeaders.long.team)}
                dropdownButtonAriaLabel={intl.formatMessage(CommonMessages.ariaLabels.select.team.dropdownButton)}
                onOptionChange={handleModalTeamUpdated}
                options={teamOptions}
                optionsBoxAriaLabel={intl.formatMessage(CommonMessages.ariaLabels.select.team.optionsBox)}
                selectedOption={teamOptions.find((to) => to.value === resolvedValues.resolvedTeam) || null}
            />
        ),
        [intl, handleModalTeamUpdated, teamOptions, resolvedValues]
    );

    const modalLeagueOptionsSelect = useMemo(
        () => (
            <FilterSelect
                aria-label={intl.formatMessage(CommonMessages.tableHeaders.long.league)}
                dropdownButtonAriaLabel={intl.formatMessage(CommonMessages.ariaLabels.select.league.dropdownButton)}
                onOptionChange={handleModalLeagueUpdated}
                options={leagueOptions}
                optionsBoxAriaLabel={intl.formatMessage(CommonMessages.ariaLabels.select.league.optionsBox)}
                selectedOption={leagueOptions.find((lo) => lo.value === resolvedValues.resolvedLeague) || null}
            />
        ),
        [intl, handleModalLeagueUpdated, leagueOptions, resolvedValues.resolvedLeague]
    );

    const modalAmateurTeamOptionsSelect = useMemo(
        () => (
            <FilterSelect
                aria-label={intl.formatMessage(CommonMessages.tableHeaders.long.amateurTeam)}
                dropdownButtonAriaLabel={intl.formatMessage(
                    CommonMessages.ariaLabels.select.amateurTeam.dropdownButton
                )}
                onOptionChange={handleModalAmateurTeamUpdated}
                options={amateurTeamOptions}
                optionsBoxAriaLabel={intl.formatMessage(CommonMessages.ariaLabels.select.amateurTeam.optionsBox)}
                selectedOption={
                    amateurTeamOptions.find((ao) => ao.value === resolvedValues.resolvedAmateurTeam) || null
                }
            />
        ),
        [intl, handleModalAmateurTeamUpdated, amateurTeamOptions, resolvedValues.resolvedAmateurTeam]
    );

    const modalCountryOptionsSelect = useMemo(
        () => (
            <FilterSelect
                aria-label={intl.formatMessage(CommonMessages.tableHeaders.long.country)}
                dropdownButtonAriaLabel={intl.formatMessage(CommonMessages.ariaLabels.select.country.dropdownButton)}
                onOptionChange={handleModalCountryUpdated}
                options={countryOptions}
                optionsBoxAriaLabel={intl.formatMessage(CommonMessages.ariaLabels.select.country.optionsBox)}
                selectedOption={countryOptions.find((co) => co.value === resolvedValues.resolvedCountry) || null}
            />
        ),
        [intl, handleModalCountryUpdated, countryOptions, resolvedValues.resolvedCountry]
    );

    const modalPositionOptionsSelect = useMemo(
        () => (
            <FilterSelect
                aria-label={intl.formatMessage(CommonMessages.playerInfoDescriptions.long.position)}
                dropdownButtonAriaLabel={intl.formatMessage(CommonMessages.ariaLabels.select.position.dropdownButton)}
                onOptionChange={handleModalPositionUpdated}
                options={positionOptions}
                optionsBoxAriaLabel={intl.formatMessage(CommonMessages.ariaLabels.select.position.optionsBox)}
                selectedOption={positionOptions.find((po) => po.value === resolvedValues.resolvedPosition) || null}
            />
        ),
        [intl, handleModalPositionUpdated, positionOptions, resolvedValues.resolvedPosition]
    );

    const mainFilters: Array<Filter> = [
        {
            filterName: DraftFilters.YEAR,
            filterLabel: intl.formatMessage(CommonMessages.tableHeaders.long.year),
            filterSelect: yearOptionsSelect,
        },
        {
            filterName: DraftFilters.ROUND,
            filterLabel: intl.formatMessage(CommonMessages.tableHeaders.long.round),
            filterSelect: roundOptionsSelect,
        },
    ];

    const secondaryFilters: Array<Filter> = [
        {
            filterName: DraftFilters.TEAM,
            filterLabel: intl.formatMessage(CommonMessages.tableHeaders.long.team),
            filterSelect: teamOptionsSelect,
            modalFilterSelect: modalTeamOptionsSelect,
        },
        {
            filterName: DraftFilters.LEAGUE,
            filterLabel: intl.formatMessage(CommonMessages.tableHeaders.long.league),
            filterSelect: leagueOptionsSelect,
            modalFilterSelect: modalLeagueOptionsSelect,
        },
        {
            filterName: DraftFilters.AMATEUR_TEAM,
            filterLabel: intl.formatMessage(CommonMessages.tableHeaders.long.amateurTeam),
            filterSelect: amateurTeamOptionsSelect,
            modalFilterSelect: modalAmateurTeamOptionsSelect,
        },
        {
            filterName: DraftFilters.COUNTRY,
            filterLabel: intl.formatMessage(CommonMessages.tableHeaders.long.country),
            filterSelect: countryOptionsSelect,
            modalFilterSelect: modalCountryOptionsSelect,
        },
        {
            filterName: DraftFilters.POSITION,
            filterLabel: intl.formatMessage(CommonMessages.playerInfoDescriptions.long.position),
            filterSelect: positionOptionsSelect,
            modalFilterSelect: modalPositionOptionsSelect,
        },
    ];

    return (
        <>
            {!isMedium && (
                <>
                    <DialogContainer $expandedFilters={expandedFilters} />
                    <SmallScreenFilters $expandedFilters={expandedFilters} ref={dialogRef}>
                        <FilterModalHeading>
                            <h4 aria-level={2}>
                                <FormattedMessage {...CommonMessages.misc.callToAction.moreFilters} />
                            </h4>
                            <CloseIcon onClick={handleModalClose} />
                        </FilterModalHeading>
                        {formatFilters(secondaryFilters)}
                        <FilterActionContainer $expandedFilters={expandedFilters}>
                            <FilterButton variant={ButtonType.CTA} onClick={clearFilters} disabled={disableClearFilter}>
                                <FormattedMessage
                                    description="A message describing a user's ability to clear selected filters"
                                    defaultMessage="Clear Filters"
                                />
                            </FilterButton>
                            <FilterButton variant={ButtonType.CTA} onClick={applyFilters} disabled={disableApplyFilter}>
                                <FormattedMessage
                                    description="A message describing a user's ability to apply selected filters"
                                    defaultMessage="Apply Filters"
                                />
                            </FilterButton>
                        </FilterActionContainer>
                    </SmallScreenFilters>
                </>
            )}
            <FiltersContainer>
                {formatFilters(mainFilters)}
                {isMedium && formatFilters(secondaryFilters)}
                {!isMedium && (
                    <FilterButton
                        variant={ButtonType.Text}
                        onClick={() => setExpandedFilters(!expandedFilters)}
                        disableHover={true}
                    >
                        <FormattedMessage {...CommonMessages.misc.callToAction.moreFilters} />
                    </FilterButton>
                )}
            </FiltersContainer>
        </>
    );
};

export default Filters;
